<template>
  <div id="app" class="container-fluid">
    <full_cwc_head :visit_id="visit_id" />

    <div class="main-content">
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading growth records...</p>
      </div>
      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchRecords">Retry</button>
      </div>
      <div v-else class="card">
        <div class="card-header">
          <h3><i class="fas fa-chart-line"></i> Growth Monitoring</h3>
          <button class="btn-edit-header" @click="openAddModal">
            <i class="fas fa-plus"></i> Add Reading
          </button>
        </div>
        <div class="card-body">
          <div v-if="!records.length" class="no-data">No growth readings recorded yet.</div>
          <div v-else class="table-responsive">
            <table class="table-custom">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Age (mo)</th>
                  <th>Weight</th>
                  <th>Height</th>
                  <th>MUAC</th>
                  <th>HC</th>
                  <th>Nutritional Status</th>
                  <th>Next Appt</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in records" :key="record.id">
                  <td>{{ formatDateTime(record.recorded_at) }}</td>
                  <td>{{ record.age_months ?? "-" }}</td>
                  <td>{{ record.weight_kg ? record.weight_kg + " kg" : "-" }}</td>
                  <td>{{ record.height_cm ? record.height_cm + " cm" : "-" }}</td>
                  <td>{{ record.muac_cm ? record.muac_cm + " cm" : "-" }}</td>
                  <td>{{ record.head_circumference_cm ? record.head_circumference_cm + " cm" : "-" }}</td>
                  <td>
                    <span :class="statusClass(record.nutritional_status)">{{
                      record.nutritional_status || "-"
                    }}</span>
                  </td>
                  <td>{{ formatDate(record.next_appointment_date) }}</td>
                  <td>
                    <button class="btn-icon" title="Edit" @click="openEditModal(record)">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn-icon btn-icon-danger" title="Delete" @click="deleteRecord(record.id)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <GrowthRecordModal
      v-if="showModal"
      :visitId="visit_id"
      :editRecord="editingRecord"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script>
import full_cwc_head from "./full_cwc_head.vue";
import GrowthRecordModal from "./GrowthRecordModal.vue";
import { generalApiRequest } from "../../../../api/apiIndex";

export default {
  name: "CwcGrowthMonitoring",
  components: { full_cwc_head, GrowthRecordModal },
  data() {
    return {
      loading: false,
      error: null,
      visit_id: null,
      records: [],
      showModal: false,
      editingRecord: null,
    };
  },
  mounted() {
    this.visit_id = this.$route.params.visit_id;
    if (this.visit_id) {
      this.fetchRecords();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchRecords() {
      this.loading = true;
      this.error = null;
      try {
        const response = await generalApiRequest(`cwc_growth_records/visit/${this.visit_id}`, "GET", {}, 1);
        if (response.success) {
          this.records = response.data || [];
        } else {
          this.error = response.message || "Failed to load growth records";
        }
      } catch (err) {
        console.error("Error fetching growth records:", err);
        this.error = "An error occurred while loading growth records. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    openAddModal() {
      this.editingRecord = null;
      this.showModal = true;
    },
    openEditModal(record) {
      this.editingRecord = record;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingRecord = null;
    },
    async handleSaved() {
      this.closeModal();
      await this.fetchRecords();
    },
    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this growth reading?")) return;
      try {
        const response = await generalApiRequest(`cwc_growth_records/${id}`, "DELETE", {}, 1);
        if (response.success) {
          this.records = this.records.filter((r) => r.id !== id);
        } else {
          alert(response.message || "Failed to delete growth reading");
        }
      } catch (error) {
        console.error("Error deleting growth record:", error);
        alert("Failed to delete growth reading. Please try again.");
      }
    },
    statusClass(status) {
      if (status === "Severe malnutrition") return "status-positive";
      if (status === "Moderate malnutrition") return "status-warning";
      return "";
    },
    formatDateTime(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }
.container-fluid { padding: 12px; background-color: #f5f7fa; min-height: 100vh; }
.main-content { margin-top: 12px; }
.loading-container, .error-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4rem 2rem; text-align: center; background: white; border-radius: 10px; }
.loading-container i { font-size: 3rem; color: #276749; margin-bottom: 1rem; }
.error-container i { font-size: 3rem; color: #dc3545; margin-bottom: 1rem; }
.btn-retry { background: #276749; color: white; border: none; padding: 10px 24px; border-radius: 6px; font-weight: 600; cursor: pointer; }
.card { background: white; border-radius: 10px; border: 1px solid #e9ecef; box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden; }
.card-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-bottom: 2px solid #dee2e6; }
.card-header h3 { margin: 0; font-size: 16px; font-weight: 600; color: #495057; display: flex; align-items: center; gap: 8px; }
.card-header h3 i { color: #276749; }
.btn-edit-header { background: linear-gradient(135deg, #48bb78 0%, #276749 100%); color: white; border: none; padding: 7px 16px; border-radius: 6px; font-weight: 600; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.card-body { padding: 16px 20px; }
.no-data { text-align: center; padding: 20px; color: #6c757d; font-size: 13px; font-style: italic; background: #f8f9fa; border-radius: 6px; }
.table-responsive { overflow-x: auto; }
.table-custom { width: 100%; border-collapse: collapse; font-size: 12px; }
.table-custom th { background: #f8f9fa; padding: 10px 8px; text-align: left; font-size: 11px; font-weight: 600; color: #6c757d; text-transform: uppercase; border-bottom: 2px solid #dee2e6; white-space: nowrap; }
.table-custom td { padding: 8px; border-bottom: 1px solid #e9ecef; color: #495057; white-space: nowrap; }
.table-custom tbody tr:hover { background: #f8f9fa; }
.status-positive { color: #dc3545; font-weight: 600; }
.status-warning { color: #b7791f; font-weight: 600; }
.btn-icon { background: none; border: none; color: #276749; cursor: pointer; padding: 4px 6px; }
.btn-icon-danger { color: #dc3545; }
</style>
